import { X } from "lucide-react";

import { Button } from "@/components/ui";
import { RunStatus, type UseCaseRun } from "@/types/content";
import { parseRunInputs } from "@/utils/run";
import { formatDuration } from "@/views/Videos/utils";

interface Props {
  run: UseCaseRun;
  onClose: () => void;
}

const isImageLike = (value: unknown): value is string =>
  typeof value === "string" &&
  (value.startsWith("data:image") || /\.(png|jpe?g|webp)$/i.test(value));

const formatValue = (value: unknown): string => {
  if (isImageLike(value)) return "image";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  const str = String(value ?? "");
  return str.length > 120 ? `${str.slice(0, 120)}…` : str;
};

export const RunDetailsModal = ({ run, onClose }: Props) => {
  const inputs = Object.entries(parseRunInputs(run.inputs_json)).filter(
    ([, v]) => v != null && v !== "",
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="mx-4 max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-semibold text-lg text-zinc-900">Details</h3>
          <Button size="icon" variant="ghost" onClick={onClose} className="rounded-full">
            <X />
          </Button>
        </div>

        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-zinc-500">Type</span>
            <span className="text-zinc-700">
              {run.task_type_label ?? run.task_type_id}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-500">Status</span>
            <span
              className={`font-medium ${
                run.status === RunStatus.FAILED
                  ? "text-red-600"
                  : run.status === RunStatus.COMPLETED
                    ? "text-emerald-600"
                    : "text-zinc-700"
              }`}
            >
              {run.status}
            </span>
          </div>
          {run.duration_seconds != null && (
            <div className="flex justify-between">
              <span className="text-zinc-500">Duration</span>
              <span className="text-zinc-700">
                {formatDuration(run.duration_seconds)}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-zinc-500">Created</span>
            <span className="text-zinc-700">
              {new Date(run.created_at).toLocaleString()}
            </span>
          </div>

          {inputs.length > 0 && (
            <div className="border-zinc-100 border-t pt-3">
              <p className="mb-2 font-medium text-xs text-zinc-500">Inputs</p>
              <div className="space-y-2">
                {inputs.map(([key, value]) => (
                  <div key={key} className="flex justify-between gap-3">
                    <span className="text-zinc-500 capitalize">
                      {key.replace(/_/g, " ")}
                    </span>
                    <span className="max-w-[60%] truncate text-right text-zinc-700">
                      {formatValue(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {run.error && (
            <div className="rounded-lg bg-red-50 p-3">
              <p className="mb-1 font-medium text-red-700 text-xs">Error</p>
              <p className="break-all text-red-600 text-xs">{run.error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
